import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiMiniBellAlert } from 'react-icons/hi2';
import { BsInfoCircle } from 'react-icons/bs';
import { HiLink, HiKey, HiPlusCircle, HiOutlineClipboardDocument } from 'react-icons/hi2';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { SessionData } from './types';
import ProjectSelector, { ProjectSelectorHandle } from './ProjectSelector';
import { useDebounce } from '../../../hooks/useDebounce';
import api from '../../../api';

type TeamJoinMethod = 'link' | 'code' | 'create';

interface TeamModeContentProps {
  sessionData: SessionData;
  setSessionData: React.Dispatch<React.SetStateAction<SessionData>>;
  teamTab: 'create' | 'join';
  setTeamTab: React.Dispatch<React.SetStateAction<'create' | 'join'>>;
  roomCode: string;
  setRoomCode: React.Dispatch<React.SetStateAction<string>>;
  onCreateRoom: () => void;
  onJoinRoom: (roomCode?: string) => void;
  onManageProjects?: () => void;
  onCreateProjects?: () => void;
  projectSelectorRef?: React.RefObject<ProjectSelectorHandle | null>;
}



/** Extract room code from invite link (e.g. ?room=ABC123 or /room/ABC123) */
const extractRoomCodeFromLink = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const url = new URL(trimmed);
      const room = url.searchParams.get('room') || url.searchParams.get('roomId') || url.searchParams.get('code');
      if (room) return room.toUpperCase();
    }

    // Check path like /room/ABC123, /join/ABC123, or /team/ABC-123 without http
    const pathMatch = trimmed.match(/\/(?:room|join|team)\/([A-Za-z0-9\-]+)(?:[/?#]|$)/i);
    if (pathMatch) return pathMatch[1];
  } catch {
    // Ignore parse errors, return null below
  }

  return null;
};

const TeamModeContent: React.FC<TeamModeContentProps> = ({
  sessionData,
  setSessionData,
  teamTab,
  setTeamTab,
  roomCode,
  setRoomCode,
  onCreateRoom,
  onJoinRoom,
  onManageProjects,
  onCreateProjects,
  projectSelectorRef,
}) => {
  const navigate = useNavigate();
  const [joinMethod, setJoinMethod] = useState<TeamJoinMethod>('link');
  const [joinInput, setJoinInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const hasInitializedJoinFromParent = React.useRef(false);

  // Prefill join input when coming from invite link (parent's roomCode set from URL)
  useEffect(() => {
    if (teamTab === 'join' && roomCode && !hasInitializedJoinFromParent.current) {
      setJoinInput(roomCode);
      hasInitializedJoinFromParent.current = true;
    }
  }, [teamTab, roomCode]);

  // Pre-generate room code when on Create tab so user can share before creating
  useEffect(() => {
    const fetchRoomCode = async () => {
      try {
        const response = await api.focus.generateTeamRoomId();
        setSessionData(prev => ({ ...prev, roomCode: response.roomId }));
      } catch (error) {
        console.error('Failed to pre-generate room code:', error);
      }
    };

    if (teamTab === 'create' && !sessionData.roomCode) {
      fetchRoomCode();
    }
  }, [teamTab, sessionData.roomCode, setSessionData]);
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [localCustomDuration, setLocalCustomDuration] = useState(sessionData.customDuration || '');

  useEffect(() => {
    setLocalCustomDuration(sessionData.customDuration || '');
  }, [sessionData.customDuration]);

  const debouncedCustomDuration = useDebounce(localCustomDuration, 500);

  useEffect(() => {
    if (debouncedCustomDuration !== (sessionData.customDuration || '')) {
      if (debouncedCustomDuration === '' || /^\d+$/.test(debouncedCustomDuration)) {
        setSessionData(prev => ({
          ...prev,
          customDuration: debouncedCustomDuration,
          duration: debouncedCustomDuration ? parseInt(debouncedCustomDuration) : 0,
        }));
      }
    }
  }, [debouncedCustomDuration, setSessionData]);

  useEffect(() => {
    if (sessionData.duration <= 25) {
      if (sessionData.breakReminders) {
        setSessionData(prev => ({ ...prev, breakReminders: false }));
      }
    } else {
      if (!sessionData.breakReminders) {
        setSessionData(prev => ({ ...prev, breakReminders: true }));
      }
    }
  }, [sessionData.duration, setSessionData]);

  const handleJoinRoomSubmit = () => {
    const code = joinMethod === 'link'
      ? extractRoomCodeFromLink(joinInput)
      : joinInput.trim();
    if (code) {
      setRoomCode(code);
      onJoinRoom(code);
      navigate(`/team/${code}`);
    } else {
      alert(joinMethod === 'link'
        ? 'Please paste a valid invite link'
        : 'Please enter a room code (4–12 characters)');
    }
  };

  const canJoin = joinMethod === 'link'
    ? extractRoomCodeFromLink(joinInput) !== null
    : joinInput.trim().length >= 4;

  return (
    <div className="w-full min-h-screen bg-[#FFF8F0] pt-2 sm:pt-4 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-4xl sm:text-5xl font-medium text-gray-900">
            Team Focus Room
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 font-light">
            Focus together, achieve more — like a clubhouse
          </p>
        </div>

        {/* Join / Create Tabs */}
        <div className="flex justify-center">
          <div className="bg-white border border-gray-200/60 rounded-2xl p-1.5 inline-flex shadow-sm">
            <button
              onClick={() => setTeamTab('join')}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${teamTab === 'join'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Join Room
            </button>
            <button
              onClick={() => setTeamTab('create')}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${teamTab === 'create'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Create Room
            </button>
          </div>
        </div>

        {/* Join Room Section */}
        {teamTab === 'join' && (
          <div className="bg-white border border-gray-200/60 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
            <p className="text-gray-600 text-center">
              Join via invite link shared by someone, or enter the room code directly
            </p>

            {/* Join method toggle */}
            <div className="flex gap-2 p-1 bg-gray-50 rounded-xl">
              <button
                onClick={() => setJoinMethod('link')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${joinMethod === 'link'
                  ? 'bg-white text-orange-600 shadow-sm border border-orange-200/60'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <HiLink className="text-base" />
                Via Link
              </button>
              <button
                onClick={() => setJoinMethod('code')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${joinMethod === 'code'
                  ? 'bg-white text-orange-600 shadow-sm border border-orange-200/60'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <HiKey className="text-base" />
                Room ID
              </button>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {joinMethod === 'link'
                  ? 'Paste invite link'
                  : 'Enter room code'}
              </label>
              <input
                type="text"
                value={joinInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setJoinInput(val);
                  if (joinMethod === 'link' && val.trim() !== '') {
                    const linkRegex = /^(?:https?:\/\/)?(?:www\.)?[^\/]+\/team\/[a-zA-Z0-9\-]+$/;
                    if (!linkRegex.test(val.trim())) {
                      setLinkError('Invalid link format. Expected: <domain>/team/<room-id>');
                    } else {
                      setLinkError('');
                    }
                  } else {
                    setLinkError('');
                  }
                }}
                placeholder={joinMethod === 'link'
                  ? `${typeof window !== 'undefined' ? window.location.origin : 'https://app.example.com'}/team/<room-id>`
                  : 'e.g. ABC123'}
                className={`w-full px-5 py-4 bg-white border rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:outline-none transition-all text-base ${linkError && joinMethod === 'link'
                  ? 'border-red-500 focus:ring-red-400/30 focus:border-red-500'
                  : 'border-gray-200/60 focus:ring-orange-400/30 focus:border-orange-300/50'
                  }`}
              />
              {linkError && joinMethod === 'link' && (
                <p className="text-red-500 text-sm mt-1">{linkError}</p>
              )}
            </div>

            <button
              onClick={handleJoinRoomSubmit}
              disabled={!canJoin || (joinMethod === 'link' && !!linkError)}
              className={`w-full py-4 rounded-xl font-medium text-base transition-all ${canJoin && !(joinMethod === 'link' && !!linkError)
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
            >
              Join Focus Room
            </button>
          </div>
        )}

        {/* Create Room Section - No focus sounds */}
        {teamTab === 'create' && (
          <div className="space-y-8">
            <div className="bg-white border border-gray-200/60 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
              {/* Room name */}
              <div className="space-y-2">
                <label className="block text-lg text-gray-700 font-normal">Room name</label>
                <input
                  type="text"
                  value={sessionData.roomName}
                  onChange={(e) => setSessionData({ ...sessionData, roomName: e.target.value })}
                  placeholder="e.g. Design Team Focus"
                  className="w-full px-5 py-4 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all text-base"
                />
              </div>

              {/* Focus Area */}
              <div className="space-y-2">
                <ProjectSelector
                  ref={projectSelectorRef}
                  selectedProjectId={sessionData.projectId}
                  onProjectChange={(projectId) => setSessionData({ ...sessionData, projectId })}
                  showManageButton={true}
                  onManageClick={onManageProjects}
                  onCreateClick={onCreateProjects}
                  required={true}
                />
              </div>

              {/* What will you focus on? */}
              <div className="space-y-2">
                <label className="block text-lg text-gray-700 font-normal">
                  What will you focus on?
                </label>
                <input
                  type="text"
                  value={sessionData.goal}
                  onChange={(e) => setSessionData({ ...sessionData, goal: e.target.value })}
                  placeholder="e.g. Finish today's top task, Prepare meeting notes"
                  className="w-full px-5 py-4 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all text-base"
                />
              </div>

              {/* Focus Duration - No music in team mode */}
              <div className="space-y-2">
                <label className="block text-lg text-gray-700 font-normal">
                  Focus Duration
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  {[25, 45, 60].map((duration) => (
                    <button
                      key={duration}
                      onClick={() => {
                        setSessionData({ ...sessionData, duration, customDuration: '' });
                        setShowCustomDuration(false);
                      }}
                      className={`px-7 py-3.5 rounded-full font-normal transition-all duration-300 text-base ${sessionData.duration === duration && !showCustomDuration
                        ? 'bg-orange-50 text-orange-600 border border-orange-200/40'
                        : 'bg-white text-gray-600 border border-gray-200/40 hover:border-orange-200/60 hover:text-orange-600/80'
                        }`}
                    >
                      {duration}m
                    </button>
                  ))}
                  {!showCustomDuration ? (
                    <button
                      onClick={() => {
                        setShowCustomDuration(true);
                        setLocalCustomDuration('');
                        setSessionData({ ...sessionData, customDuration: '', duration: 0 });
                      }}
                      className="px-4 py-3.5 text-sm text-gray-500 hover:text-orange-600 transition-colors font-normal"
                    >
                      + Custom time
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={localCustomDuration}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setLocalCustomDuration(value);
                          }
                        }}
                        placeholder="Minutes"
                        className="px-5 py-3.5 rounded-full border-2 border-orange-300 text-gray-900 bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 focus:outline-none text-base transition-all w-28 text-center font-normal shadow-sm"
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          setShowCustomDuration(false);
                          setSessionData({ ...sessionData, customDuration: '', duration: 25 });
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-normal"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Share with team - Room ID & Invite Link */}
              {sessionData.roomCode && (
                <div className="bg-orange-50/50 border border-orange-200/40 rounded-2xl p-5 space-y-4">
                  <h4 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <HiOutlineClipboardDocument className="text-orange-500" />
                    Share with your team
                  </h4>
                  <p className="text-sm text-gray-600">
                    Share this room ID or invite link so others can join before you start.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Room ID:</span>
                      <code className="flex-1 px-3 py-2 bg-white border border-gray-200/60 rounded-lg text-sm font-mono text-gray-900 truncate">
                        {sessionData.roomCode}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(sessionData.roomCode);
                          alert('Room ID copied!');
                        }}
                        className="px-3 py-2 bg-white border border-orange-200/60 hover:bg-orange-50 text-orange-600 rounded-lg text-sm font-medium shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <button
                        onClick={() => {
                          const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/team?room=${sessionData.roomCode}`;
                          navigator.clipboard.writeText(link);
                          alert('Invite link copied!');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-orange-200/60 hover:bg-orange-50 text-orange-600 rounded-lg text-sm font-medium"
                      >
                        <HiLink className="text-base shrink-0" />
                        Copy Invite Link
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Break Reminders */}
              <div className="bg-white border border-gray-200/40 rounded-2xl p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <HiMiniBellAlert className="text-orange-500/70 text-base" />
                      <h4 className="text-sm font-normal text-gray-600">Gentle check-ins</h4>
                    </div>
                    <p className="text-xs text-gray-400 font-light leading-relaxed">
                      Soft reminders to breathe, relax posture, and stay present
                      <TooltipProvider>
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <span className="inline-block ml-1 align-middle cursor-help">
                              <BsInfoCircle className="text-gray-400 hover:text-orange-500 transition-colors text-xs" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-xs text-justify">
                              Gentle nudges to breathe, adjust posture, or reset attention — without breaking your focus.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </p>
                  </div>
                  <button
                    onClick={() => setSessionData({ ...sessionData, breakReminders: !sessionData.breakReminders })}
                    disabled={sessionData.duration <= 25}
                    className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${sessionData.breakReminders ? 'bg-orange-500' : 'bg-gray-300/60'} ${sessionData.duration <= 25 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${sessionData.breakReminders ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </button>
                </div>
              </div>

              {/* CTA - Create Room */}
              <button
                onClick={onCreateRoom}
                disabled={sessionData.duration < 1}
                className={`w-full py-4 rounded-2xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2 ${sessionData.duration < 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
              >
                <HiPlusCircle className="text-xl" />
                Create Focus Room
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamModeContent;
